import { useState } from 'react';
import { Play } from 'lucide-react';
import { BlockRecap } from './BlockRecap';
import { Button, Modal } from '@ai-tutor/ui';
import { ThoughtForToday } from '../thoughts/ThoughtForToday';
import type { Thought } from '../thoughts/thoughts';
import { BreakActivities, type BreakActivity } from './BreakActivities';
import type { BreakKind } from '@/lib/focus/focusMachine';
import {
  blockHeadline,
  blockSummary,
  type FinishedBlockView,
} from './focusViewModel';

export interface BreakSheetProps {
  open: boolean;
  onClose: () => void;
  block: FinishedBlockView;
  breakKind: BreakKind;
  /** Break time left, or `null` once the break is over. */
  timeLabel: string | null;
  /** Left out when there is none to show. */
  thought?: Thought | null;
  onSaveRecap: (text: string) => void;
  onDismissRecap: () => void;
  onBackToFocus: () => void;
  onDone: () => void;
  /** Stories open a specific activity. */
  defaultActivity?: BreakActivity;
}

/**
 * The break, as a sheet over whatever page you were on: a word on the block
 * you just finished, an optional one-line recap (recalling what you learned
 * is itself practice), something restful to do, and the thought for today.
 *
 * Opened from the toast or the pill, never forced open: the break runs either
 * way, and interrupting someone mid-sentence to tell them to rest is not rest.
 */
export function BreakSheet({
  open,
  onClose,
  block,
  breakKind,
  timeLabel,
  thought,
  onSaveRecap,
  onDismissRecap,
  onBackToFocus,
  onDone,
  defaultActivity = 'breathe',
}: BreakSheetProps) {
  const [activity, setActivity] = useState<BreakActivity>(defaultActivity);
  const long = breakKind === 'long';
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={blockHeadline(block)}
      size='lg'
    >
      <div className='space-y-5 px-6 pb-6 pt-5'>
        <div className='-mt-2 space-y-1'>
          <p className='text-sm text-muted-foreground'>{blockSummary(block)}</p>
          {block.awayMinutes > 0 && (
            <p className='text-xs text-muted-foreground'>
              Your block ended {block.awayMinutes} min ago, while you were away.
            </p>
          )}
        </div>
        <BlockRecap
          block={block}
          onSave={onSaveRecap}
          onDismiss={onDismissRecap}
        />
        <div className='space-y-3 border-t border-border/60 pt-4'>
          <div className='flex items-baseline justify-between'>
            <h3 className='text-sm font-semibold text-foreground'>
              {long ? 'Long break' : 'Your break'}
            </h3>
            <span className='font-mono text-sm tabular-nums text-muted-foreground'>
              {timeLabel ? `${timeLabel} left` : 'Break over'}
            </span>
          </div>
          <BreakActivities activity={activity} onActivityChange={setActivity} />
        </div>
        {thought && <ThoughtForToday thought={thought} compact />}
        <div className='flex flex-wrap justify-end gap-2 border-t border-border/60 pt-4'>
          <Button className='max-sm:h-11' variant='ghost' onClick={onDone}>
            I&apos;m done for now
          </Button>
          <Button className='max-sm:h-11' onClick={onBackToFocus}>
            <Play className='mr-2 h-4 w-4' aria-hidden='true' />
            Back to focus
          </Button>
        </div>
      </div>
    </Modal>
  );
}
